import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, Paper } from '@mui/material';
import { ExpandLess, ExpandMore, Map as MapIcon } from '@mui/icons-material';

import { CONFIG } from '@frontend/config';
import { RouteMapPoint, routeMapSrc } from '../routeMap';
import { RouteMapCard } from './RouteMapCard';

interface Props {
  /** El vertical decide cómo se dibuja el trayecto (o si no se dibuja). */
  vertical: string;
  /**
   * Los puntos de la búsqueda COMPROMETIDA — los mismos que el buscador ya
   * arma para el modal de reserva, no los del formulario en edición: mientras
   * el asesor edita sin buscar, el mapa sigue describiendo la búsqueda que
   * produjo los resultados que tiene delante.
   */
  points?: RouteMapPoint[] | null;
}

/**
 * El recorrido de la BÚSQUEDA en la vista de resultados: uno por búsqueda, en
 * el cromo, colapsado. No es un mapa por card — eso sigue prohibido: serían N
 * iframes por búsqueda y se comería el presupuesto de densidad de la lista.
 *
 * Colapsado no existe: la card (y con ella el iframe) se monta recién al
 * abrir, así que una búsqueda que nadie expande no le cuesta un solo request
 * al proveedor de mapas. Abierto una vez, se queda montado: cerrar y volver a
 * abrir no vuelve a cargarlo.
 *
 * "Cada búsqueda nueva arranca colapsado" no se resuelve acá: el contenedor lo
 * monta con la identidad de la búsqueda comprometida como `key`, así que una
 * búsqueda nueva es un componente nuevo —colapsado, y sin el iframe de la
 * anterior— en vez de un `useEffect` que persiga cambios de props.
 */
export function SearchRouteMap({ vertical, points }: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  // La MISMA decisión que dibuja la card: el control no puede ofrecer abrir un
  // mapa que después no se renderiza.
  const src = routeMapSrc({
    vertical,
    points,
    apiKey: CONFIG.MAPS_EMBED_KEY,
    language: i18n.language,
  });
  if (!src) return null;

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
      <Button
        size="small"
        onClick={() => setOpen((current) => !current)}
        startIcon={<MapIcon fontSize="small" />}
        endIcon={open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        sx={{ textTransform: 'none', fontWeight: 600 }}
        aria-expanded={open}
      >
        {t(open ? 'travelServices.routeMap.hide' : 'travelServices.routeMap.show')}
      </Button>
      {/* `mountOnEnter` sin `unmountOnExit`: no existe hasta la primera vez que
          se abre, y después sobrevive a los cierres. */}
      <Collapse in={open} mountOnEnter>
        <Box pt={1}>
          <RouteMapCard vertical={vertical} points={points} />
        </Box>
      </Collapse>
    </Paper>
  );
}
